import posthog from "posthog-js";
import type { DateRange } from "react-day-picker";

import type { Filter, FilterValue } from "@/components/StructuredSearch/config";

type TrackingValue = string | number | boolean | null | undefined;
export type TrackingProperties = Record<string, TrackingValue>;

const getDateRangeDays = (dateRange: DateRange | undefined): number | undefined => {
  if (!dateRange?.from || !dateRange.to) {
    return undefined;
  }

  const milliseconds = dateRange.to.getTime() - dateRange.from.getTime();
  if (!Number.isFinite(milliseconds)) {
    return undefined;
  }

  return Math.max(0, Math.round(milliseconds / (24 * 60 * 60 * 1000)));
};

const getValueKind = (value: FilterValue): string => {
  if (Array.isArray(value)) {
    return "array";
  }
  if (typeof value === "object" && value !== null) {
    return "object";
  }
  return typeof value;
};

const getValueCount = (value: FilterValue): number => {
  if (Array.isArray(value)) {
    return value.length;
  }
  return 1;
};

export const trackRequestsAnalyticsEvent = (
  eventName: string,
  properties: TrackingProperties = {},
) => {
  posthog.capture(eventName, {
    source: "requests_analytics",
    ...properties,
  });
};

export const getDateRangeTrackingProperties = (
  dateRange: DateRange | undefined,
): TrackingProperties => ({
  has_date_range: Boolean(dateRange?.from && dateRange.to),
  date_range_days: getDateRangeDays(dateRange),
});

export const getStructuredFilterTrackingProperties = (filter: Filter): TrackingProperties => ({
  filter_field: filter.field,
  filter_field_type: filter.type,
  filter_operator: filter.operator,
  has_nested_key: Boolean(filter.nestedKey),
  filter_value_kind: getValueKind(filter.value),
  filter_value_count: getValueCount(filter.value),
});

export const getStructuredQueryTrackingProperties = ({
  filters,
  q,
  dateRange,
}: {
  filters: Filter[];
  q: string;
  dateRange: DateRange | undefined;
}): TrackingProperties => ({
  ...getDateRangeTrackingProperties(dateRange),
  filter_count: filters.length,
  filter_fields: filters.map(filter => filter.field).join(","),
  filter_operators: filters.map(filter => filter.operator).join(","),
  has_search_text: q.trim().length > 0,
  search_text_length: q.trim().length,
});

export const serializeStructuredFilterForTracking = (filter: Filter): string =>
  JSON.stringify({
    id: filter.id,
    field: filter.field,
    type: filter.type,
    operator: filter.operator,
    hasNestedKey: Boolean(filter.nestedKey),
  });
